import DownloadIcon from '@mui/icons-material/Download';
import { Button, Stack } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadPopup from '/@/components/bayer/download-popup';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import Icon from '/@/components/icon';
import Spinner from '/@/components/spinner';
import { useUnitProvider } from '/@/core/context/unit';
import { type Breadcrumb, useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useCropMonitoringNotes } from '/@/hooks/crop-monitoring-notes';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useReports } from '/@/hooks/reports';
import { useGetHistoricalWeather } from '/@/hooks/weather';
import { _ } from '/@/locale';
import { generateReportPDF } from '/@/store/crop-monitoring/apiProviders';
import { getAppInstance, getSessionLanguage } from '/@/store/session/selectors';
import type { Brand } from '/@/types/catalog';
import type {
    CropMonitoring,
    CropMonitoringApplicationReports,
    CropMonitoringResultNotes,
} from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import type { AppInstance, Units } from '../../../../types/core';
import CropMonitoringReportApplications from './components/applications';
import ReportFooter from './components/footer';
import ReportHeaderData from './components/header';
import ReportMetrics from './components/metrics';
import CropMonitoringReportNotes from './components/notes';
import CropMonitoringReportRecommendations from './components/recommendations';
import PrecipitationReportGraphic from './components/report-graphics/precipitation';
import TemperaturesReportGraphic from './components/report-graphics/temperatures';

export const FieldMonitoringReportContent = ({
    cropMonitoring,
    historicalWeather,
    field,
    cropMonitoringResultNotesQuery,
    applicationReportsQuery,
    appInstance,
    isPdf = false,
    unitsProps = undefined,
    breadcrumbs = [],
    logo,
}: {
    cropMonitoring: CropMonitoring;
    historicalWeather: HistoricalWeather;
    field: Field;
    cropMonitoringResultNotesQuery: CropMonitoringResultNotes;
    applicationReportsQuery: CropMonitoringApplicationReports;
    appInstance: AppInstance;
    isPdf: boolean;
    unitsProps?: {
        brands: Brand[];
        language: string;
        units: Units;
    };
    breadcrumbs: Breadcrumb[];
    logo?: string;
    breadcrumbs: Breadcrumb[];
}) => {
    const language = useSelector(getSessionLanguage);
    const [downloadPDF, setDownloadPDF] = useState(false);

    const plantingYear = new Date(cropMonitoring?.plantingDate).getFullYear();
    const harvestYear = cropMonitoring?.harvestDate ? new Date(cropMonitoring.harvestDate).getFullYear() : plantingYear;

    const hideRecommendedOptions = !cropMonitoring?.active;
    const units = useUnitProvider(unitsProps);

    const locationLine = [convertAreaFromM2(field?.area || 0, units).toString(), field?.location].join(' - ');

    const reportSections = (
        <>
            <ReportHeaderData
                unitsProp={unitsProps}
                cropMonitoring={cropMonitoring}
                report={cropMonitoring?.result}
                field={field}
                plantingYear={plantingYear}
                harvestYear={harvestYear}
            />
            <ReportMetrics cropMonitoring={cropMonitoring} unitsProp={unitsProps} isPdf={isPdf} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap='nowrap'>
                <Stack sx={{ width: { xs: '100%', md: '50%' } }}>
                    <PrecipitationReportGraphic
                        isPdf={isPdf}
                        unitsProp={unitsProps}
                        cropMonitoring={cropMonitoring}
                        historicalWeather={historicalWeather}
                        width={450}
                    />
                </Stack>
                <Stack sx={{ width: { xs: '100%', md: '50%' } }}>
                    <TemperaturesReportGraphic
                        isPdf={isPdf}
                        unitsProp={unitsProps}
                        cropMonitoring={cropMonitoring}
                        historicalWeather={historicalWeather}
                        width={450}
                    />
                </Stack>
            </Stack>

            <CropMonitoringReportApplications
                cropMonitoring={cropMonitoring}
                applicationReportsQuery={applicationReportsQuery}
                isPdf={isPdf}
            />
            <CropMonitoringReportNotes
                cropMonitoring={cropMonitoring}
                cropMonitoringResultNotesQuery={cropMonitoringResultNotesQuery}
                isPdf={isPdf}
            />
            {!isPdf && !hideRecommendedOptions && (
                <CropMonitoringReportRecommendations fieldId={field?.id} appInstance={appInstance} />
            )}
            {isPdf && <ReportFooter />}
        </>
    );

    // Both web and PDF use the shared Header. The PDF (rendered server-side via
    // renderToStaticMarkup) drops the breadcrumbs and shows the logo on the right
    // instead of the download action.
    const header = (
        <Header
            title={`${_('Reporte de cosecha')} ${field?.name ?? ''}`.trim()}
            subtitle={locationLine}
            breadcrumbs={isPdf ? undefined : breadcrumbs}
            chips={[
                ...(field?.externalProviderSynced
                    ? [{ label: _('Sincronizado con Fieldview'), icon: <Icon type={'fieldview'} size='xxs' /> }]
                    : []),
                ...(field?.campaign ? [{ label: `${_('Campaña')} ${field.campaign}` }] : []),
            ]}
            actions={
                isPdf
                    ? [
                          <img
                              key='logo'
                              src={logo}
                              alt='Logo'
                              style={{ width: 180, height: 40, objectFit: 'contain' }}
                          />,
                      ]
                    : [
                          <Button
                              key='download-pdf'
                              variant='contained'
                              color='secondary'
                              startIcon={<DownloadIcon />}
                              sx={{
                                  width: { xs: '100%', md: 'auto' },
                                  whiteSpace: 'nowrap',
                                  minWidth: 'fit-content',
                              }}
                              onClick={() => {
                                  setDownloadPDF(true);
                              }}
                          >
                              {_('Descargar PDF')}
                          </Button>,
                      ]
            }
        />
    );

    return (
        <PageContainer header={header}>
            <Stack direction='column' spacing={3} pb={5} mt={4}>
                {reportSections}
            </Stack>
            {!isPdf && (
                <DownloadPopup
                    visible={downloadPDF}
                    title={_('Descargá tu reporte de cosecha')}
                    onClose={() => setDownloadPDF(false)}
                    onDownloadBefore={async (f) => {
                        if (f) {
                            return f;
                        }
                        const file = await generateReportPDF({
                            cropMonitoringId: cropMonitoring?.id,
                            language,
                        });
                        return file;
                    }}
                    onDownloadAfter={() => {
                        setDownloadPDF(false);
                    }}
                    beforeDownload={_('Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.')}
                    afterDownload={_(
                        '¡Tu reporte de cosecha ya está listo!\nUna vez descargado, lo vas a encontrar en tu carpeta de Descargas.'
                    )}
                />
            )}
        </PageContainer>
    );
};

const FieldMonitoringReport = ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    const cropMonitoringQuery = useCropMonitoringById(cropMonitoringId);
    const historicalWeatherQuery = useGetHistoricalWeather(
        cropMonitoringId,
        {},
        { enabled: Boolean(cropMonitoringId) }
    );
    const fieldQuery = useFieldById(
        { id: cropMonitoringQuery.data?.field },
        { enabled: Boolean(cropMonitoringQuery.data?.field) }
    );
    const cropMonitoringResultNotesQuery = useCropMonitoringNotes({
        field: cropMonitoringQuery.data?.field,
        closedCropMonitoring: cropMonitoringQuery.data?.id,
        ordering: '-created',
    });
    const applicationReportsQuery = useReports(
        { fieldId: cropMonitoringQuery.data?.field, type: 'application' },
        { enabled: Boolean(cropMonitoringQuery.data?.field) }
    );
    const appInstance = useSelector(getAppInstance);
    const breadcrumbs = useBreadcrumbs('fieldMonitoring.ReportDetail');

    if (cropMonitoringQuery.isLoading || fieldQuery.isLoading)
        return (
            <Stack height='100%' alignItems='center' justifyContent='center'>
                <Spinner />
            </Stack>
        );
    return (
        <FieldMonitoringReportContent
            cropMonitoring={cropMonitoringQuery.data}
            historicalWeather={historicalWeatherQuery.data}
            field={fieldQuery.data}
            cropMonitoringResultNotesQuery={cropMonitoringResultNotesQuery}
            applicationReportsQuery={applicationReportsQuery}
            appInstance={appInstance}
            isPdf={false}
            breadcrumbs={breadcrumbs}
        />
    );
};

export default FieldMonitoringReport;
